// Shopify CSV -> core orders. Ported from shopify.gs (Channel "EG").
// Rows sharing the order Name are one order; continuation line-item rows have
// blank order-level fields. Fixes the legacy refund-mapping bug and maps the
// province -> county (both confirmed).

import { SOURCE } from '../constants';
import { parseCsvText } from './parseCsv';
import { decodeHtmlEntities, cleanPostcode, parseMoney, normalisePhone } from '../lib/clean';
import { parseShopifyDate } from '../lib/dates';
import { shopifyDeliveryService } from '../lib/delivery';
import { codeToCountry } from '../lib/countries';

function makeGetter(headerRow) {
  const idx = {};
  headerRow.forEach((h, i) => {
    idx[String(h).trim().toLowerCase()] = i;
  });
  return (row, name) => {
    const i = idx[name.toLowerCase()];
    return i == null ? '' : row[i] ?? '';
  };
}

function rowToObj(headerRow, row) {
  const obj = {};
  headerRow.forEach((h, i) => {
    obj[h] = row[i] ?? '';
  });
  return obj;
}

// Lineitem name -> { product, variation } (legacy split on first "-").
function splitLineItemName(value) {
  if (!value) return { product: '', variation: '' };
  const stripCase = (s) => s.replace(/felt sleeve case/gi, '').trim();
  if (value.includes('-')) {
    const [head, ...rest] = value.split('-');
    return { product: stripCase(head), variation: decodeHtmlEntities(rest.join('-').trim()) };
  }
  return { product: stripCase(value), variation: '' };
}

export function parseShopifyOrders(text, rules) {
  const rows = parseCsvText(text);
  if (!rows.length) return [];
  const header = rows[0];
  const get = makeGetter(header);

  const groups = new Map(); // order Name -> rows
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row || row.every((c) => !c || String(c).trim() === '')) continue;
    const name = String(get(row, 'Name')).trim();
    if (!name) continue;
    if (!groups.has(name)) groups.set(name, []);
    groups.get(name).push(row);
  }

  const orders = [];
  for (const [name, itemRows] of groups) {
    const first = itemRows[0];
    const orderId = name.startsWith('#') ? name.slice(1) : name;
    const countryCode = String(get(first, 'Shipping Country')).trim().toUpperCase();
    const billingName = decodeHtmlEntities(get(first, 'Billing Name'));

    const lineItems = itemRows.map((row, n) => {
      const rawName = get(row, 'Lineitem name');
      const { product, variation } = splitLineItemName(rawName);
      return {
        lineId: `li_${n + 1}`,
        product,
        productRaw: rawName,
        variation,
        buyerNotes: decodeHtmlEntities(get(first, 'Notes')),
        quantity: Number(get(row, 'Lineitem quantity')) || 1,
        unitPrice: parseMoney(get(row, 'Lineitem price')),
        sku: get(row, 'Lineitem sku') || '',
        isLeather: rawName.toLowerCase().includes('leather'),
        sortOrder: n,
      };
    });

    orders.push({
      source: SOURCE.SHOPIFY,
      platformOrderId: orderId,
      platformOrderId2: String(get(first, 'Id')).trim(),
      buyer: {
        userId: billingName, // Shopify has no username; legacy used Billing Name
        name: billingName,
        email: get(first, 'Email'),
      },
      shipping: {
        name: decodeHtmlEntities(get(first, 'Shipping Name')),
        phone: normalisePhone(get(first, 'Shipping Phone')),
        address1: decodeHtmlEntities(get(first, 'Shipping Address1')),
        address2: decodeHtmlEntities(get(first, 'Shipping Address2')),
        townCity: decodeHtmlEntities(get(first, 'Shipping City')),
        county:
          decodeHtmlEntities(get(first, 'Shipping Province Name')) ||
          decodeHtmlEntities(get(first, 'Shipping Province')), // newly mapped
        postcode: cleanPostcode(get(first, 'Shipping Zip'), countryCode),
        country: codeToCountry(countryCode),
        countryCode,
        service: shopifyDeliveryService(get(first, 'Shipping Method'), rules?.shopify),
        ioss: false, // Shopify carries no IOSS flag
      },
      lineItems,
      money: {
        currency: get(first, 'Currency') || 'GBP',
        postage: parseMoney(get(first, 'Shipping')),
        discount: parseMoney(get(first, 'Discount Amount')),
        refund: parseMoney(get(first, 'Refunded Amount')), // legacy bug: never mapped
      },
      dates: {
        ordered: parseShopifyDate(get(first, 'Created at')),
        paid: parseShopifyDate(get(first, 'Paid at')) || null,
        postByPlatform: null,
      },
      raw: { shopify: itemRows.map((r) => rowToObj(header, r)) },
    });
  }

  return orders;
}
